/* eslint-disable no-magic-numbers */
import each from "lodash/each";
import moment from "moment-timezone";
import apiFactory from "@/helpers/api";
import piggyBack from "@/helpers/piggyBack";
import MeetingState from "@/constants/MeetingState";
import UnpinOperationType from "@/constants/UnpinOperationType";
import { resolveOutcomeStatus } from "@/store/modules/outcomeHelpers";

const {
	post: meetingAPIPost,
	put: meetingAPIPut,
	del: meetingAPIDelete,
	get: meetingAPIGet,
} = apiFactory();

export const saveOutcomeCompletion = ( { outcomeId, isCompleted, meetingId, topicId, }, options = { version: "2", } ) => {
	const body = {
		id: outcomeId,
		status: resolveOutcomeStatus( isCompleted, options.version ),
		meetingId,
		topicId,
	};

	const url = `/outcome/${ outcomeId }`;

	return meetingAPIPut(
		url,
		body,
		{ "x-api-version": options.version, }
	).then( response => {
		return { ...response, };
	} );
};

export const saveOneOnOneCommitment = async ( { commitment, }, options = { version: "2", } ) => {
	const url = `/outcome/${ commitment.id }`;
	return meetingAPIPut(
		url,
		commitment,
		{ "x-api-version": options.version, }
	).then( response => {
		return { ...response, };
	} );
};

// using piggyBack means that any requests with the exact same args within 1 second
// will only result in one API call (though both will act as if they executed independently)
export const getMeeting = piggyBack(
	( { meetingId, }, options = { version: "2", } ) => {
		return meetingAPIGet(
			`/meeting/${ meetingId }`,
			{},
			{ "x-api-version": options.version, }
		).then( response => {
			// eslint-disable-next-line no-magic-numbers
			const start = moment( response.plannedStart * 1000 );
			// eslint-disable-next-line no-magic-numbers
			const end = moment( response.plannedEnd * 1000 ); // another date
			const duration = moment.duration( start.diff( end ) );
			const hours = Math.abs( duration.asHours() );

			return {
				...response,
				// TODO: remove patches
				isRecurring: !!response.meetingSeriesId,
				hours,
			};
		} );
	}
);

export const getMeetingByCode = ( { code, }, options = { version: "2", } ) => {
	return meetingAPIGet(
		`/meeting/by-code/${ code }`,
		{},
		{ "x-api-version": options.version, }
	);
};

export const getMeetingsByWeekOffset = ( { offset = 0, includeExternalEvents = false, }, options = { version: 2, } ) => {
	const params = {};
	// includeExternalEvents is a boolean parameter;
	// its presence is the positive indicator regardless of value
	if ( includeExternalEvents ) {
		params.includeExternalEvents = 1;
	}
	if ( options.version === 1 ) {
		params.offset = offset;
	}
	if ( options.version === 2 ) {
		params.weekOffset = offset;
	}

	return meetingAPIGet(
		"/meeting",
		params,
		{ "x-api-version": options.version, }
	);
};

export const updateMeeting = ( { meetingId, displayState, meetingState, } ) => {
	const url = `/meeting/${meetingId}`;
	return meetingAPIPut( url, {
		displayState,
		meetingState,
	} );
};

export const sendTopicRequest = ( { meetingId, }, options = { version: 2, } ) => {
	const url = `/meeting/${meetingId}/topicrequest`;
	return meetingAPIPost( url, {}, {
		"x-api-version": options.version,
	} );
};

export const createTopic = ( { meetingId, topic, } ) => {
	const url = `/meeting/${ meetingId }/topic`;
	return meetingAPIPost( url, topic, { "x-api-version": 2, } ).then( createdTopic => {
		return {
			outcomes: [],
			...createdTopic,
		};
	} );
};

export const updateTopic = ( { meetingId, topic, } ) => {
	const url = `/meeting/${ meetingId }/topic/${ topic.id }`;
	return meetingAPIPut( url, topic, { "x-api-version": 2, } ).then( updatedTopic => {
		return {
			outcomes: [],
			...updatedTopic,
		};
	} );
};

export const sortTopics = ( { meetingId, topicIds, } ) => {
	const url = `/meeting/${ meetingId }/topic/sort`;
	return meetingAPIPost( url, { topicIds, }, { "x-api-version": 2, } );
};

export const deferTopic = ( { meetingId, topicId, } ) => {
	const url = `/meeting/${meetingId}/topic/${topicId}/deferral`;
	return meetingAPIPost( url, {}, { "x-api-version": 2, } );
};

export const undeferTopic = ( { meetingId, topicId, } ) => {
	const url = `/meeting/${meetingId}/topic/${topicId}/deferral`;
	return meetingAPIDelete( url, {}, { "x-api-version": 2, } );
};

export const removeTopic = ( { meetingId, topicId, } ) => {
	const url = `/meeting/${ meetingId }/topic/${ topicId }`;
	return meetingAPIDelete( url, {}, { "x-api-version": 2, } );
};

export const pinTopic = ( { meetingId, topicId, } ) => {
	const url = `/meeting/${meetingId}/topic/${topicId}/pin`;
	return meetingAPIPost( url, {}, { "x-api-version": 2, } );
};

export const unpinFutureTopics = ( { meetingId, topicId, } ) => {
	const url = `/meeting/${meetingId}/topic/${topicId}/pin`;
	return meetingAPIDelete( url, {}, { "x-api-version": 2, } );
};

export const unpinOnlyThisTopic = ( { meetingId, topicId, } ) => {
	const url = `/meeting/${meetingId}/topic/${topicId}/pin`;
	return meetingAPIPut( url, { operationType: UnpinOperationType.THIS, }, { "x-api-version": 2, } );
};

export const unpinThisAndFutureTopics = ( { meetingId, topicId, } ) => {
	const url = `/meeting/${meetingId}/topic/${topicId}/pin`;
	return meetingAPIPut( url, { operationType: UnpinOperationType.THIS_AND_FUTURE, }, { "x-api-version": 2, } );
};

export const createTeamMeetingAssociation = ( { meetingId, teamId, }, options = { version: 2, } ) => {
	const url = `/meeting/${meetingId}/team/${ teamId }`;
	return meetingAPIPost( url, {}, {
		"x-api-version": options.version,
	} );
};

export const deleteTeamMeetingAssociation = ( { meetingId, teamId, }, options = { version: 2, } ) => {
	const url = `/meeting/${meetingId}/team/${ teamId }`;
	return meetingAPIDelete( url, {}, {
		"x-api-version": options.version,
	} );
};

export const fetchOutcomes = ( params, options = { version: 2, } ) => {
	const url = "/outcome";
	/* params for reference
	const params = {
		meetingId,
		meetingSeriesId,
		teamId,
		dueAfter,
		dueBefore,
		completedAfter,
		completedBefore,
		outcomeType = "",
		outcomeStatus = "",
		personId: filterPersonId,
		sortType = "",
	};
	*/
	const processedParams = {};
	each( params, ( value, key ) => {
		if ( value !== undefined ) {
			processedParams[ key ] = value;
		}
	} );
	const headers = { "x-api-version": options.version, };
	return meetingAPIGet( url, processedParams, headers );
};

export const fetchMeetingSeries = ( {
	meetingSeriesId,
	plannedStartFrom,
	offset = 0,
	limit = 10,
}, options = {
	version: 2,
} ) => {
	const url = "/meeting";
	const params = {
		meetingSeriesId,
		offset,
		limit,
		plannedStartFrom,
		sortDir: "DESC",
	};
	const headers = { "x-api-version": options.version, };
	return meetingAPIGet( url, params, headers );
};

export const startMeeting = ( { meetingId, }, options = { version: 2, } ) => {
	const url = `/meeting/${meetingId}`;
	const body = {
		meetingState: MeetingState.MEETING_STARTED,
	};
	return meetingAPIPut( url, body, {
		"x-api-version": options.version,
	} );
};

export const stopMeeting = ( { meetingId, }, options = { version: 2, } ) => {
	const url = `/meeting/${meetingId}`;
	const body = {
		meetingState: MeetingState.MEETING_ENDED,
	};
	return meetingAPIPut( url, body, {
		"x-api-version": options.version,
	} );
};

export const joinMeeting = ( { meetingId, } ) => {
	const url = `/meeting/${meetingId}/attendee`;
	return meetingAPIPost( url );
};

export const leaveMeeting = ( { meetingId, } ) => {
	const url = `/meeting/${meetingId}/attendee`;
	return meetingAPIDelete( url );
};

export const fetchAttendees = ( { meetingId, }, options = { version: 2, } ) => {
	const url = `/meeting/${meetingId}/attendee`;
	const headers = {
		"x-api-version": options.version,
	};
	return meetingAPIGet( url, {}, headers );
};

export const fetchMeetingNote = ( { meetingId, } ) => {
	const url = `/meeting/${meetingId}/note`;
	return meetingAPIGet( url );
};

export const saveMeetingNote = ( { meetingId, noteContent, isNew, } ) => {
	const url = `/meeting/${meetingId}/note`;
	return isNew ?
		meetingAPIPost( url, { notes: noteContent, } ) :
		meetingAPIPut( url, { notes: noteContent, } );
};

/**
 * @return {Promise<ScheduleMeeting>}
 */
export const setCurrentTopic = ( { meetingId, topicId, }, options = { version: 2, } ) => {
	const url = `/meeting/${meetingId}`;
	const headers = {
		"x-api-version": options.version,
	};
	return meetingAPIPut( url, { currentTopicId: topicId, }, headers );
};

export const getOutcome = ( { outcomeId, }, options = { version: 2, } ) => {
	const url = `/outcome/${ outcomeId }`;
	const headers = {
		"x-api-version": options.version,
	};
	return meetingAPIGet( url, headers );
};

export const createOutcome = ( { newOutcome, }, options = { version: 2, } ) => {
	const url = "/outcome";
	const headers = {
		"x-api-version": options.version,
	};
	return meetingAPIPost( url, newOutcome, headers );
};

export const updateOutcome = ( { meetingId, outcome, }, options = { version: 2, } ) => {
	const url = `/outcome/${outcome.id}`;
	const headers = {
		"x-api-version": options.version,
	};
	return meetingAPIPut( url, { ...outcome, meetingId, }, headers );
};

export const finalizeMeetingReport = ( { meetingId, }, options = { version: 2, } ) => {
	const url = `/meeting/${meetingId}`;
	const headers = {
		"x-api-version": options.version,
	};
	const body = {
		outcomeDelay: 0,
	};
	return meetingAPIPut( url, body, headers );
};

export const deleteOutcome = ( { meetingId, outcomeId, }, options = { version: 2, } ) => {
	const url = `/outcome/${outcomeId}`;
	const headers = {
		"x-api-version": options.version,
	};
	return meetingAPIDelete( url, { meetingId, }, headers );
};

export const fetchUpcomingTeamMeetings = ( { teamId, timezone, }, options = { version: 2, } ) => {
	const url = "/meeting";
	const now = moment().tz( timezone );
	const plannedStartFrom = now
		.toISOString();
	const plannedStartTo = now
		.clone()
		.startOf( "date" )
		.add( 7, "days" )
		.toISOString();
	const params = {
		teamId,
		plannedStartFrom,
		plannedStartTo,
	};
	const headers = { "x-api-version": options.version, };
	return meetingAPIGet( url, params, headers );
};

export const fetchRecentTeamMeetings = ( { teamId, timezone, }, options = { version: 2, } ) => {
	const url = "/meeting";
	const now = moment().tz( timezone );
	const plannedStartFrom = now
		.clone()
		.startOf( "date" )
		.subtract( 7, "days" )
		.toISOString();
	const plannedStartTo = now
		.toISOString();
	const params = {
		teamId,
		plannedStartFrom,
		plannedStartTo,
	};
	const headers = { "x-api-version": options.version, };
	return meetingAPIGet( url, params, headers );
};



// WEBPACK FOOTER //
// ./src/api/meetingAPI.js